import { ScaleLinear, scaleLinear, ScaleThreshold } from 'd3-scale';

import {
    Color,
    colorArrayToRgb,
    ColorRange,
    grey11,
    grey14DimAnchors,
    setDeckGLAlpha,
    warm15Dispersed,
    warm15DispersedAnchors,
    warm2Dispersed,
    warm2Targets
} from '@methanesat/colors';

import { alpha, PositionedLabelProps, Theme } from '@methanesat/ui-components';
import { AreaEmissionsProducts, MethaneLayerIds, Platforms, RASTER_COLORMAP_NAMES } from '../types';
import { createLinearColorScaleArray, makeLinearColorScale } from '../utils/colorScales';
import { PLATFORM_RASTER_SIZE } from './data';

/**
 * Ranges for total emissions and dispersed sources (formerly known as area emissions)
 * for MAIR and MSAT.
 *
 * Dispersed sources (area emissions) - Max value correlates to *largest physically possible*
 * kg/hr value.  This enables us to tell the story "is this area dirty relative to other areas?"
 *
 * Target ranges -  Max value correlates to the basin with the highest kg/hr.
 * This enables us to tell the story "is this target dirty compared to other targets?"
 *
 * Note: "dirtiest" is defined as highest absolute kg/kr.  In the past we instead used
 * mean emissions which normalized by area (mean = total emissions / ground area), but users
 * found this confusing.  Ideally we would use the more industry-standard methane intensity
 * (intensity = total emissions / total production in barrels of oil equivalent), but we
 * don't have production data worldwide, so we wouldn't be able to color-code all targets
 * using this.
 *
 * More context: both absolute (total kg/hr) and relative (methane intensity) emissions
 * are important to take into account.  Absolute emissions tell us how much an area is
 * contributing to overall worldwide emissions, while relative emissions give us a way
 * to compare two dissimilar regions, usually using production as a normalizer (similar
 * to how per capita is used when comparing a populous vs nonpopulous country).
 */
export const EMISSIONS_RANGES = {
    [Platforms.MAIR]: {
        [MethaneLayerIds.targets]: {
            MIN: 10_000,
            MAX: 140_000
        },
        [MethaneLayerIds.areaEmissionRaster]: {
            MIN: 0,
            MAX: 23
        }
    },
    [Platforms.MSAT]: {
        [MethaneLayerIds.targets]: {
            MIN: 5_000,
            MAX: 400_000 /** Via Kira on Slack Oct 16: "Ritesh said they expect the top end to be between 300K and 500K kg/hour" */
        }
    }
};

export const MIN_SIGNIFICANT_RASTER_EMISSION = {
    [Platforms.MAIR]: 1,
    [Platforms.MSAT]: 1
};

/**
 *
 * TARGETS
 *
 */
export const TARGET_PALETTE_MISSING_DATA = grey11;

export const TARGET_GREY_DATA = '#808080';

export const targetColorStopCount = 5;

export const getTargetEmissionsRange = (platform: Platforms) => {
    return EMISSIONS_RANGES[platform][MethaneLayerIds.targets];
};

export const DEFAULT_TARGET_FILL_COLOR: Color = [0, 0, 0, 0];

const createTargetDomain = (platform: Platforms) => {
    const { MIN, MAX } = getTargetEmissionsRange(platform);
    return [MIN, MAX];
};
/** domains (arrays of color stop thresholds) to pass to d3 scale for both MAIR and MSAT targets*/
export const TARGET_DOMAINS = {
    [Platforms.MAIR]: createTargetDomain(Platforms.MAIR),
    [Platforms.MSAT]: createTargetDomain(Platforms.MSAT)
};

// The summarized palette is used for icons to show a subset of the target palette
export const SUMMARIZED_TARGET_PALETTE = createLinearColorScaleArray(5, warm2Targets);

/**
 * Returns the array of colors used for target totals
 */
export const getTargetTotalsColors = (theme: Theme, enabled: boolean) => {
    return SUMMARIZED_TARGET_PALETTE.map((color) => {
        const rgbColor = colorArrayToRgb(color);

        // When disabled, the colors become paler, which is why the alpha function is used
        return enabled ? rgbColor : alpha(rgbColor, theme.palette.action.disabledOpacity);
    });
};

export const getColorFromScale = (value: number, scale: ScaleThreshold<number, Color> | ScaleLinear<Color, Color>) => {
    return scale(value);
};

/**
 * Returns a color value, given a TargetFeature with the totalkKgHr property
 */
export function getTargetColor(totalKgHr: number | undefined, platform: Platforms = Platforms.MAIR) {
    const targetColorScale = makeLinearColorScale(TARGET_DOMAINS[platform], warm2Targets);
    const color = getColorFromScale(totalKgHr || 0, targetColorScale);
    return color;
}

export const getFilledTargetColor = (totalMethane?: number, platform?: Platforms) => {
    return setDeckGLAlpha(getTargetColor(totalMethane, platform), 255 * 0.85);
};

/**
 * Returns the proportion of an emission value relative to the max for a given platform (0 to 1).
 */
const mapTargetTotalToProportionOfMax = (value: number, platform: Platforms) => {
    const { MIN, MAX } = getTargetEmissionsRange(platform);
    return Math.min((value - MIN) / (MAX - MIN), 1);
};

/**
 * Maps each color on the target palette to percentages specified in the input labels, allowing
 * for interpolation across both linearly and non-linearly spaced labels. This enables us to
 * stretch/compress the color gradient used on a scale in certain places if necessary.
 *
 * @param labels: Array of `ColorStopLabel`.
 * @example
 * interpolateColorScalePercentages([{ label: '10,000', percent: 0 }, { label: '30,000', percent: 50 }, { label: '140,000', percent: 100 }])
 *  -> [
 *       { percent: 0, color: 'rgb(9, 1, 121)' },
 *       { percent: 50, color: 'rgb(160, 6, 143)' },
 *       { percent: 55.56, color: 'rgb(182, 43, 121)' },
 *       ...
 *     ]
 */
export const interpolateColorScalePercentages = (labels: ColorStopLabel[], platform: Platforms) => {
    const targetColorArray = createLinearColorScaleArray(labels.length, warm2Targets);

    const labelProportions = labels.map((l) => {
        const val = parseFloat(l.label.replace(/,/g, ''));
        return mapTargetTotalToProportionOfMax(val, platform);
    });

    const scale = scaleLinear()
        .domain(labelProportions)
        .range(labels.map((l) => l.percent));

    const colorPercentages = targetColorArray.map((c, index) => ({
        percent: scale(index / (targetColorArray.length - 1)),
        color: colorArrayToRgb(c)
    }));

    return colorPercentages;
};

/**
 *
 * DISPERSED SOURCES
 *
 */
export const warmLinearDispersed = createLinearColorScaleArray(14, warm2Dispersed);

// This array gives us the anchors for where the colors change on the color bar
export const warmLinearDispersedAnchors = [0, 9, 13, 17, 25, 35, 45, 55, 65, 75, 85, 90, 95, 100];

// This array gives us transparency levels for L4 MAIR dispersed colors since createLinearColorScaleArray
// does not calculate variances in transparency
const mairDispersedAlphaVlues = [77, 77, 102, 230, 230, 230, 230, 230, 230, 230, 230, 230, 230, 230];

export const warmLinearDispersedWithAlpha = warmLinearDispersed.map((arr, index) => [
    ...arr,
    mairDispersedAlphaVlues[index]
]) as Color[];

// Default color palettes for dispersed sources
export const DISPERSED_SOURCE_PALETTE = {
    [Platforms.MAIR]: warmLinearDispersedWithAlpha,
    [Platforms.MSAT]: warm15Dispersed
};

export const RASTER_COLORMAP_DEFS: {
    [key: string]: {
        MIN: number;
        MAX: number;
        /** These are real values associated with the low ends of each item in the color array.
         * Should match the values in `msat-eoapi`.
         *
         * May be different than values of METHANE_BINS, given the challenges of converting a binned color
         * scale to a gradient.
         *
         * TODO: add color scales to these definitions, rather than having the only association
         * to the correct RASTER_COLORMAP_NAME be the [key: string]
         */
        COLOR_BIN_THRESHOLDS: number[];
        /** **For building color gradients:** Real values between or including MIN & MAX, each associated with an ANCHOR_PERCENTAGE */
        METHANE_BINS: number[];
        /** **For building color gradients:** Percentages of the scale, each associated with a METHANE_BINS value */
        ANCHOR_PERCENTAGES: number[];
        /**
         * Optional custom labels that anchored to specific points on the scale.
         * This option will override default min/max labels in BarGradient, so it should
         * include those explicity, if those are necessary as well.
         */
        LABELS?: PositionedLabelProps[];
    };
} = {
    [RASTER_COLORMAP_NAMES.msatWarmDefault]: {
        MIN: 0,
        MAX: 20,
        COLOR_BIN_THRESHOLDS: [0, 0.000001, 0.25, 0.5, 1, 1.5, 2.25, 3.25, 4.5, 6, 7.75, 9.75, 12, 14.5, 17],
        METHANE_BINS: [0.000001, 0.25, 0.5, 1, 1.5, 2.25, 3.25, 4.5, 6, 7.75, 9.75, 12, 14.5, 17, 20],
        ANCHOR_PERCENTAGES: warm15DispersedAnchors,
        LABELS: [
            // position of value 1 in METHANE_BINS (METHANE_BINS[2] === 1)
            { label: 1, position: warm15DispersedAnchors[3] },
            { label: '20+', position: 100 }
        ]
    },
    [RASTER_COLORMAP_NAMES.msatWarmExtended]: {
        MIN: 0,
        MAX: 100,
        COLOR_BIN_THRESHOLDS: [0, 0.000001, 1, 2, 3, 4, 6, 8, 10, 12, 15, 25, 50, 75, 100],
        METHANE_BINS: [0, 0.000001, 1, 2, 3, 4, 6, 8, 10, 12, 15, 25, 50, 75, 100],
        ANCHOR_PERCENTAGES: warm15DispersedAnchors,
        LABELS: [
            // position of value 1 in METHANE_BINS (METHANE_BINS[2] === 1)
            { label: 1, position: warm15DispersedAnchors[2] },
            { label: '100+', position: 100 }
        ]
    },
    [RASTER_COLORMAP_NAMES.msatDimState]: {
        MIN: 0,
        MAX: 100,
        COLOR_BIN_THRESHOLDS: [0, 1, 2, 3, 4, 6, 8, 10, 12, 15, 25, 50, 75, 100],
        METHANE_BINS: [0, 1, 2, 3, 4, 6, 8, 10, 12, 15, 25, 50, 75, 100],
        ANCHOR_PERCENTAGES: grey14DimAnchors
    },
    [RASTER_COLORMAP_NAMES.msatCoolLog]: {
        MIN: 1880,
        MAX: 2280,
        COLOR_BIN_THRESHOLDS: [1880, 1901, 1931, 1976, 2042, 2138],
        METHANE_BINS: [1880, 1901, 1931, 1976, 2042, 2138, 2280],
        ANCHOR_PERCENTAGES: Array(7).map((_v, idx) => (idx * 100) / 7)
    }
} as const;

export type MSATL4RasterColorMaps = RASTER_COLORMAP_NAMES.msatWarmDefault | RASTER_COLORMAP_NAMES.msatWarmExtended;

export const isMSATL4RasterColormapName = (name: unknown): name is MSATL4RasterColorMaps => {
    if (typeof name !== 'string') return false;

    return Object.values([RASTER_COLORMAP_NAMES.msatWarmDefault, RASTER_COLORMAP_NAMES.msatWarmExtended]).includes(
        name as MSATL4RasterColorMaps
    );
};

/**
 * Returns information about a given color scale
 * Depends on platform, product, which layer (target or raster),
 * and the user-selected color map name.
 *
 * Currently returns only { MIN, MAX } for anything except MSAT raster l4, which
 * also returns COLOR_BIN_THRESHOLDS by color scale.
 * May be expanded to return more complex information about each color scale.
 *
 * NOTE: this would make more sense in `utils/colors`, but must be here for the time being
 * to prevent circular dependencies. If/when we can get rid of the const MAIR_L4_COLOR_STOPS,
 * we should be able to move this back to utils/colors
 */
export const getColorScaleDefinition = (
    platform: Platforms,
    product: AreaEmissionsProducts,
    layer: MethaneLayerIds.targets | MethaneLayerIds.areaEmissionRaster,
    colorMapName?: RASTER_COLORMAP_NAMES
): {
    MIN: number;
    MAX: number;
    METHANE_BINS?: number[];
    ANCHOR_PERCENTAGES?: number[];
    COLOR_BIN_THRESHOLDS?: number[];
    LABELS?: PositionedLabelProps[];
} => {
    // use same min/max for any L3 product
    if (product === AreaEmissionsProducts.l3 && colorMapName) {
        return RASTER_COLORMAP_DEFS[colorMapName];
    }

    // mair-specific ranges
    if (platform === Platforms.MAIR) {
        return EMISSIONS_RANGES[platform][layer];
    }

    // target-specific ranges
    if (layer === MethaneLayerIds.targets) {
        return EMISSIONS_RANGES[platform][layer];
    }

    // MSAT, raster L4
    if (colorMapName) {
        return RASTER_COLORMAP_DEFS[colorMapName];
    }

    return RASTER_COLORMAP_DEFS[RASTER_COLORMAP_NAMES.msatWarmDefault];
};

const getFiveStopLinearScale = (max: number) => {
    return [100 / max, 25, 50, 75, 100]; // these are percentages 1 - 100%
};
const getMairEmissionsColorStops = (colorMapName?: RASTER_COLORMAP_NAMES, pixelSizeKm2?: number) => {
    const { MAX } = getColorScaleDefinition(
        Platforms.MAIR,
        AreaEmissionsProducts.l4,
        MethaneLayerIds.areaEmissionRaster,
        colorMapName
    );
    const pixelSizeKm2ForScaling = pixelSizeKm2
        ? pixelSizeKm2
        : Math.pow(PLATFORM_RASTER_SIZE[Platforms.MAIR][AreaEmissionsProducts.l4], 2);
    const scaledMax = MAX * pixelSizeKm2ForScaling;
    const fiveStopLinearScale = getFiveStopLinearScale(scaledMax);
    const colorStops = fiveStopLinearScale.map((n: number) => (n * scaledMax) / 100).reverse();
    return colorStops;
};

// TODO: deprecate this const when possible
export const MAIR_L4_COLOR_STOPS = getMairEmissionsColorStops();

/**
 * We want to use a logarithmic scale so that there are decreased distances
 * between colors changes at lower flux values, and increased distances at
 * higher values. This allows users to visually disambiguate between low level
 * emission targets (ranges in the tens) from high level emitters (ranges in
 * the hundreds) simultaneously.
 * For this reason the distinguishing colors are placed a long a logarithmic
 * scale. Such that exp(N/x) = 100. Where N is the number colors in the palette
 * minus 1.
 * x solves N/ln(100). Then for n values 0 - N, each step solves exp(n/x).
 * Therefore, for 5 steps we are solving exp(n/0.869)
 */

export const FIVE_STOP_LOG_SCALE = [1, 3, 10, 32, 100]; // these are percentages 1 - 100%

// Concentrations have ranges around 1800 and 2400 for preliminary data
export const CONCENTRATION_RANGE = [1880, 2280];
export const CONCENTRATION_COLOR_STOPS_LOG = FIVE_STOP_LOG_SCALE.map((n: number) => {
    const step = n === 1 ? 0 : n; // Given the min is not 0 by default, start scale at min
    const low = CONCENTRATION_RANGE[0];
    const high = CONCENTRATION_RANGE[1];
    const dif = high - low;
    // For more information on data normalizations see: https://www.indeed.com/career-advice/career-development/normalization-formula
    // Specifically: "Normalization formula for custom ranges"
    return low + step * (dif / 100); // rescale on range
}).reverse();

/**
 * Returns color anchor points for the bar gradient
 * TODO: Update with new color scales/palette changes
 */
export const getDispersedColorAnchors = (platform: Platforms, product: AreaEmissionsProducts, colors: string[]) => {
    if (product === AreaEmissionsProducts.l3) {
        return FIVE_STOP_LOG_SCALE;
    }
    if (platform === Platforms.MSAT) {
        return warm15DispersedAnchors;
    }
    // For MAIR L4, use linearly spaced anchor positions
    const n = colors.length;
    return Array.from({ length: n }, (_, i) => (i / (n - 1)) * 100);
};

export interface ColorStopLabel {
    /** Percentage for positioning. */
    percent: number;
    /** Label to display next to color stop. */
    label: string;
    /** Key for React element uniqueness. */
    key: string;
}

export interface ColorPoint {
    percent: number;
    color: string;
}

export interface ColorScales {
    [scale: string]: {
        colors: ColorPoint[];
        labels: ColorStopLabel[];
    };
}

/** Custom kg/hr values for target color scale in the mini legend. These are assigned manually
 * since the spacing between them may be irregular. They should be unique and sorted in ascending order.
 */
const mairTargetLabels: ColorStopLabel[] = [
    { percent: 0, label: '10,000', key: '10,000' },
    { percent: 33.33, label: '50,000', key: '50,000' },
    { percent: 66.67, label: '100,000', key: '100,000' },
    { percent: 100, label: '140,000', key: '140,000' }
];

const msatTargetLabels: ColorStopLabel[] = [
    { percent: 0, label: '5,000', key: '5,000' },
    { percent: 33.33, label: '140,000', key: '140,000' },
    { percent: 66.67, label: '270,000', key: '270,000' },
    { percent: 100, label: '400,000', key: '400,000' }
];

/** Mini-legend target scale labels */
export const targetScaleLabels = {
    [Platforms.MAIR]: mairTargetLabels,
    [Platforms.MSAT]: msatTargetLabels
};

/** Labels for the mini legend dispersed source default, extended range and high contrast scales. Since the color bins used for this scale
 * are non-linear and irregular, these are created manually.
 * See https://methanesat.atlassian.net/browse/DP-4151
 * for the corresponding colorbar anchor position for specific emission values.
 */
export const defaultDispersedScaleLabels: ColorStopLabel[] = [
    { percent: 100, label: '20', key: '20' },
    { percent: 80, label: '12', key: '12' },
    { percent: 50, label: '6', key: '6' },
    { percent: 30, label: '3', key: '3' },
    { percent: 8, label: '1', key: '1' }
];

export const extendedRangeDispersedScaleLabels: ColorStopLabel[] = [
    { percent: 100, label: '100', key: '100' },
    { percent: 88, label: '50', key: '50' },
    { percent: 75, label: '15', key: '15' },
    { percent: 50, label: '10', key: '10' },
    { percent: 29, label: '5', key: '5' },
    { percent: 0, label: '1', key: '1' }
];

// TODO: DP-5113 might need some adjustments when high contrast scale is integrated
export const highContrastDispersedScaleLabels: ColorStopLabel[] = [
    { percent: 100, label: '8', key: '8' },
    { percent: 80, label: '5', key: '5' },
    { percent: 50, label: '3', key: '3' },
    { percent: 30, label: '1', key: '1' }
];

interface ColorScaleData {
    anchors: number[];
    labels: ColorStopLabel[];
    gradientWidth: string;
    colorRange: ColorRange;
}

// this type lets us index into scaleData
export type ScaleDataKeys = MSATL4RasterColorMaps | RASTER_COLORMAP_NAMES.mairWarmLinear;

export const scaleData: Record<ScaleDataKeys, ColorScaleData> = {
    [RASTER_COLORMAP_NAMES.msatWarmDefault]: {
        anchors: warm15DispersedAnchors,
        labels: defaultDispersedScaleLabels,
        colorRange: warm15Dispersed,
        gradientWidth: '40%'
    },
    // TODO: [DP-5113] uncomment when high contrast scale is integrated
    // [RASTER_COLORMAP_NAMES.msatWarmHighContrast]: {
    //     anchors: warm4DispersedAnchors,
    //     labels: highContrastDispersedScaleLabels,
    //     colorRange: warm4Dispersed,
    //     gradientWidth: '20%'
    // },
    [RASTER_COLORMAP_NAMES.msatWarmExtended]: {
        anchors: warm15DispersedAnchors,
        labels: extendedRangeDispersedScaleLabels,
        colorRange: warm15Dispersed,
        gradientWidth: '80%'
    },
    [RASTER_COLORMAP_NAMES.mairWarmLinear]: {
        anchors: [],
        labels: [],
        colorRange: warmLinearDispersedWithAlpha,
        gradientWidth: ''
    }
};
